<template>
    <div class="nevs-content" v-if="loaded">
        <div class="nevs-content-top-buttons">
            <NevsButton @click="saveClick()" class="success"><i class="fa-solid fa-floppy-disk"></i>
                {{ $LANG.Get('buttons.save') }}
            </NevsButton>&nbsp;
            <NevsButton @click="backClick" class="error"><i class="fa-solid fa-arrow-left"></i> {{
                    $LANG.Get('buttons.back')
                }}
            </NevsButton>
        </div>
        <NevsCard class="client-form">
            <NevsTextField :error="clientValidation.name" :label="$LANG.Get('fields.name') + ':'"
                           v-model="client.name"></NevsTextField>
            <NevsTextField :error="clientValidation.sport_center_name" :label="$LANG.Get('fields.sportCenterName') + ':'"
                           v-model="client.sport_center_name"></NevsTextField>
            <NevsTextField :error="clientValidation.oib" :label="$LANG.Get('fields.oib') + ':'"
                           v-model="client.oib"></NevsTextField>
            <NevsTextField :error="clientValidation.official_email" :label="$LANG.Get('fields.officialEmail') + ':'"
                           v-model="client.official_email"></NevsTextField>
            <NevsTextField readonly :error="clientValidation.hash" :label="$LANG.Get('fields.hash') + ':'"
                           v-model="client.hash"></NevsTextField>
            <NevsTextField :error="clientValidation.api_token" :label="$LANG.Get('fields.apiToken') + ':'"
                           v-model="client.api_token"></NevsTextField>
            <NevsTextField :error="clientValidation.valid_until" :label="$LANG.Get('fields.validUntil') + ':'"
                           v-model="client.valid_until"></NevsTextField>
            <NevsSelect v-model="client.financial_status_id" :ajax="'select/financial-statuses'"
                        :label="$LANG.Get('fields.financialStatus')"></NevsSelect>
            <NevsNumberField :decimalPlaces="0" :error="clientValidation.discount" :label="$LANG.Get('fields.discount') + ':'"
                             v-model="client.discount"></NevsNumberField>
            <NevsNumberField :decimalPlaces="0" :error="clientValidation.notification_limit"
                             :label="$LANG.Get('fields.notificationLimit') + ':'"
                             v-model="client.notification_limit"></NevsNumberField>
            <NevsCheckbox :label="$LANG.Get('fields.plus')" v-model="client.plus"></NevsCheckbox>
            <NevsCheckbox :label="$LANG.Get('fields.intisEnabled')" v-model="client.intis_enabled"></NevsCheckbox>
            <template v-if="client.intis_enabled">
                <NevsTextField :label="$LANG.Get('fields.intisReaderTypes') + ':'"
                               v-model="intisReaderTypesText"></NevsTextField>
                <NevsTextField :label="$LANG.Get('fields.intisGateIds') + ':'"
                               v-model="intisGateIdsText"></NevsTextField>
                <NevsTextField :label="$LANG.Get('fields.intisRelay') + ':'"
                               v-model="client.intis_relay"></NevsTextField>
                <NevsNumberField :decimalPlaces="0" :label="$LANG.Get('fields.intisActivationTime') + ':'"
                                 v-model="client.intis_activation_time"></NevsNumberField>
            </template>
            <NevsCheckbox :label="$LANG.Get('fields.nukiEnabled')" v-model="client.nuki_enabled"></NevsCheckbox>
            <template v-if="client.nuki_enabled">
                <NevsTextField :label="$LANG.Get('fields.nukiApiToken') + ':'"
                               v-model="client.nuki_api_token"></NevsTextField>
                <NevsTextField :label="$LANG.Get('fields.nukiLockId') + ':'"
                               v-model="client.nuki_lock_id"></NevsTextField>
            </template>
            <NevsTextField :label="$LANG.Get('fields.latitude') + ':'"
                           v-model="client.latitude"></NevsTextField>
            <NevsTextField :label="$LANG.Get('fields.longitude') + ':'"
                           v-model="client.longitude"></NevsTextField>
            <p class="disclaimer">{{ $LANG.Get('labels.locationDisclaimer') }}</p>
            <NevsCheckbox :label="$LANG.Get('fields.automaticMemberEntries')"
                          v-model="client.automatic_member_entries"></NevsCheckbox>
            <NevsCheckbox :label="$LANG.Get('fields.stripeEnabled')" v-model="client.stripe_enabled"></NevsCheckbox>
            <NevsCheckbox v-if="client.stripe_enabled" :label="$LANG.Get('fields.paymentPlansEnabled')"
                          v-model="client.payment_plans_enabled"></NevsCheckbox>
        </NevsCard>
        <NevsCard class="invoices-card">
            <h3>{{ $LANG.Get('labels.invoices') }}</h3>
            <table class="invoices-table" v-if="invoices.length > 0">
                <thead>
                    <tr>
                        <th>{{ $LANG.Get('fields.invoiceNumber') }}</th>
                        <th>{{ $LANG.Get('fields.dateOfIssue') }}</th>
                        <th>{{ $LANG.Get('fields.dueDate') }}</th>
                        <th>{{ $LANG.Get('fields.debt') }}</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="invoice in invoices" :key="invoice.id">
                        <td>{{ invoice.number }}</td>
                        <td>{{ dateOnly(invoice.time_of_issue) }}</td>
                        <td>{{ invoice.due_date }}</td>
                        <td>{{ formatAmount(invoice.financial_status) }}</td>
                        <td class="pdf-cell">
                            <NevsButton @click="pdfClick(invoice.id)">
                                <i class="fa-solid fa-file-pdf"></i>
                                {{ $LANG.Get('tooltips.pdf') }}
                            </NevsButton>
                        </td>
                    </tr>
                </tbody>
            </table>
            <div v-else>{{ $LANG.Get('labels.noInvoices') }}</div>
        </NevsCard>
    </div>
</template>

<script>
import Client from '@/models/Client.js';
import NevsTextField from "@/components/nevs/NevsTextField.vue";
import NevsButton from "@/components/nevs/NevsButton.vue";
import NevsCard from "@/components/nevs/NevsCard.vue";
import NevsSelect from "@/components/nevs/NevsSelect.vue";
import NevsCheckbox from "@/components/nevs/NevsCheckbox.vue";
import NevsNumberField from "@/components/nevs/NevsNumberField.vue";

export default {
    name: "EntityClient",
    components: {NevsNumberField, NevsCheckbox, NevsSelect, NevsTextField, NevsButton, NevsCard},
    data() {
        return {
            loaded: false,
            client: new Client({id: this.$route.params.id}),
            clientValidation: new Client({}, true),
            changePassword: false,
            invoices: []
        }
    },
    computed: {
        // Intis reader types and gate ids are stored as integer arrays but edited as a comma-separated list.
        intisReaderTypesText: {
            get() {
                return Array.isArray(this.client.intis_reader_types) ? this.client.intis_reader_types.join(', ') : '';
            },
            set(value) {
                this.client.intis_reader_types = this.parseIntList(value);
            }
        },
        intisGateIdsText: {
            get() {
                return Array.isArray(this.client.intis_gate_ids) ? this.client.intis_gate_ids.join(', ') : '';
            },
            set(value) {
                this.client.intis_gate_ids = this.parseIntList(value);
            }
        }
    },
    methods: {
        parseIntList(value) {
            return String(value)
                .split(',')
                .map((item) => item.trim())
                .filter((item) => /^-?\d+$/.test(item))
                .map((item) => parseInt(item, 10));
        },
        formatAmount(value) {
            return Number(value).toLocaleString('hr-HR', {minimumFractionDigits: 2, maximumFractionDigits: 2}) + ' €';
        },
        dateOnly(value) {
            return value ? value.split(' ')[0] : '';
        },
        loadInvoices() {
            let vm = this;
            this.$API.APICall('get', 'client-invoices/' + this.client.id, {}, (data, success) => {
                if (success) {
                    vm.invoices = data.invoices;
                }
            });
        },
        pdfClick(id) {
            let vm = this;
            this.$API.APICall('get', 'client-invoice-pdf/' + id, {}, (data, success) => {
                if (success) {
                    const bytes = atob(data.pdf_base64);
                    const arr = new Uint8Array(bytes.length);
                    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
                    const blob = new Blob([arr], {type: 'application/pdf'});
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = data.pdf_filename || 'racun.pdf';
                    a.click();
                    URL.revokeObjectURL(url);
                } else {
                    vm.$LOCAL_BUS.TriggerEvent('popup', {text: vm.$LANG.Get('alerts.serverError'), type: 'alert'});
                }
            });
        },
        backClick() {
            if (window.history.length === 1) {
                window.close();
            } else {
                this.$router.back();
            }
        },
        saveClick() {
            let vm = this;
            // Optional, but when given it has to be an address we can write to.
            this.clientValidation.official_email = '';
            let officialEmail = (this.client.official_email || '').trim();
            if (officialEmail !== '' && !this.$HELPERS.CheckEmail(officialEmail)) {
                this.clientValidation.official_email = this.$LANG.Get('labels.invalidEmailAddress');
                return;
            }
            let payload = Object.assign({}, this.client);
            this.$API.APICall('put', 'clients/' + this.client.id, payload, (data, success) => {
                if (success) {
                    vm.$CROSS_TAB_BUS.TriggerEvent('reload-clients', {});
                    vm.$LOCAL_BUS.TriggerEvent('notification', {text: this.$LANG.Get('alerts.recordSaved')});
                    vm.backClick();
                }
            });
        }
    },
    mounted() {
        this.$store.commit('selectMenu', 'clients');
        this.$store.commit('selectSubMenu', null);

        let vm = this;
        this.$API.APICall('get', 'clients/' + this.client.id, {}, (data, success) => {
            if (success) {
                if (data.client !== null) {
                    vm.client.Fill(data.client);
                    vm.$store.commit('setBreadcrumbs', [
                        {
                            label: vm.$LANG.Get('modules.clients'),
                            link: '/clients'
                        },
                        {
                            label: vm.client.name,
                            link: null
                        }
                    ]);
                    vm.loadInvoices();
                    vm.$nextTick(()=>{
                       vm.loaded = true;
                    });
                } else {
                    vm.$LOCAL_BUS.TriggerEvent('popup', {text: vm.$LANG.Get('alerts.serverError'), type: 'alert'});
                }
            } else {
                vm.$LOCAL_BUS.TriggerEvent('popup', {text: vm.$LANG.Get('alerts.serverError'), type: 'alert'});
            }
            vm.$nextTick(() => {
                vm.loaded = true;
            });
        });
    }
}
</script>

<style scoped>
.client-form {
    max-width: 500px;
}

.disclaimer {
    margin-top: 5px;
    font-size: 0.8em;
    opacity: 0.7;
}

.invoices-card {
    max-width: 500px;
    margin-top: 10px;
}

.invoices-table {
    width: 100%;
    border-collapse: collapse;
}

.invoices-table th, .invoices-table td {
    text-align: left;
    padding: 5px;
    border-bottom: 1px solid #eee;
}

.invoices-table .pdf-cell {
    text-align: right;
    width: 1px;
    white-space: nowrap;
}
</style>
